import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errMsg } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Field } from "../ui/Field";
import { Banner } from "../ui/Banner";

type Category = { id: number; name: string };
type Created = { sku: string; name: string; quantity: number };

type Props = { open: boolean; onClose: () => void };

export function ManualProductModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [qty, setQty] = useState("0");
  const [categoryId, setCategoryId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Created | null>(null);
  const [labelBusy, setLabelBusy] = useState(false);

  const { data: categories } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/api/categories")).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setName("");
    setQty("0");
    setCategoryId("");
    setError(null);
    setDone(null);
  }, [open]);

  const createMut = useMutation({
    mutationFn: async () => {
      const quantita = Math.max(0, parseInt(qty, 10) || 0);
      const { data } = await api.post("/api/products/manual", {
        name: name.trim(),
        quantita_iniziale: quantita,
        category_id: categoryId ? parseInt(categoryId, 10) : null,
      });
      return { sku: data.sku, name: data.name, quantity: quantita } as Created;
    },
    onSuccess: (d) => {
      setDone(d);
      // Inventory + overview reflect the new product / stock-in immediately.
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["events"] });
    },
    onError: (e) => setError(errMsg(e)),
  });

  async function printLabel() {
    if (!done || labelBusy) return;
    setLabelBusy(true);
    try {
      const res = await api.get("/api/products/qr-pdf/print", {
        params: { skus: done.sku },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data as Blob);
      const w = window.open(url, "_blank");
      // Popup blocked → fall back to a direct download.
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `etichetta-${done.sku}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLabelBusy(false);
    }
  }

  const busy = createMut.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      tone={done ? "success" : "default"}
      title={done ? "Prodotto creato" : "Nuovo prodotto manuale"}
      footer={
        done ? (
          <>
            <Button variant="ghost" block onClick={onClose}>
              Chiudi
            </Button>
            <Button variant="primary" block disabled={labelBusy} onClick={printLabel}>
              <Icon name="tag" size={14} /> {labelBusy ? "Etichetta…" : "Stampa etichetta"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" block onClick={onClose} disabled={busy}>
              Annulla
            </Button>
            <Button
              variant="yellow"
              block
              disabled={busy || name.trim().length < 1}
              onClick={() => {
                setError(null);
                createMut.mutate();
              }}
            >
              <Icon name="plus" size={14} /> {busy ? "Creazione…" : "Crea prodotto"}
            </Button>
          </>
        )
      }
    >
      {error && (
        <Banner tone="danger" icon="info" style={{ marginBottom: 12 }}>
          {error}
        </Banner>
      )}

      {done ? (
        <div>
          <Banner tone="success" icon="check" style={{ marginBottom: 12 }}>
            {done.name} è stato creato con SKU{" "}
            <span className="tcg-mono" style={{ fontWeight: 800 }}>
              {done.sku}
            </span>
            {done.quantity > 0 ? ` · ${done.quantity} pezzi in stock` : " · senza stock iniziale"}.
          </Banner>
          <div style={{ fontSize: 12, color: "var(--tcg-muted)" }}>
            Stampa subito l'etichetta QR/Code128 per applicarla al prodotto fisico.
          </div>
        </div>
      ) : (
        <div>
          <Field label="Nome prodotto" hint="Obbligatorio">
            <input
              className="tcg-input"
              value={name}
              autoFocus
              placeholder="es. Bustine protettive 100pz"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Categoria" hint="Facoltativo">
            <select
              className="tcg-select"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">— Nessuna —</option>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Quantità iniziale" hint="0 = crea il prodotto senza movimento di stock">
            <input
              type="number"
              inputMode="numeric"
              min={0}
              className="tcg-input tcg-num"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
            />
          </Field>
          <div style={{ fontSize: 12, color: "var(--tcg-muted)", margin: "2px 0 12px" }}>
            Magazzino: <strong>Vicenza · Sede principale</strong>
          </div>
          <Banner tone="info" icon="info">
            Il prodotto resta solo nel hub (SKU FGC-…, nessun sync TikTok/Shopify/Odoo) e sopravvive
            al full-reset.
          </Banner>
        </div>
      )}
    </Modal>
  );
}
